import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { NgOptimizedImage } from '@angular/common';
import {TranslationService, Language, ScrollSpyService, ApiService, DataService} from '../core/services';
import { filter } from 'rxjs';
import {ScrollService} from '../core/services/scroll.service';

@Component({
  selector: 'app-navbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, NgOptimizedImage],
  template: `
    <nav
      class="fixed top-0 left-0 right-0 z-50 bg-neutral-900/95 backdrop-blur-sm border-b border-neutral-800"
      role="navigation"
      aria-label="Main navigation"
    >
      <div class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <!-- Logo -->
          <a
            routerLink="/"
            class="flex items-center gap-3 text-white font-bold text-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 rounded"
            aria-label="JKU Racing homepage"
          >
            <!-- Optimized SVG logo from /logos -->
            <img
              ngSrc="/logos/logo_on-dark-bg_borderless.svg"
              width="1500"
              height="701"
              alt="JKU Racing logo"
              class="h-8 md:h-10 w-auto"
              decoding="async"
            />
          </a>

          <!-- Desktop Navigation -->
          <div class="hidden md:flex items-center gap-1">
            <a
              routerLink="/"
              [class]="isHomeActive()
                ? 'px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'
                : 'px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'"
            >
              {{ t()('nav.home') }}
            </a>
            <!--
            <button
              type="button"
              (click)="scrollService.scrollToSection('events', '/', 12)"
              [class]="scrollSpyService.isActive('events')
                ? 'px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'
                : 'px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'"
            >
              {{ t()('nav.events') }}
            </button> -->
            <button
              type="button"
              (click)="scrollService.scrollToSection('news')"
              [class]="scrollSpyService.isActive('news')
                ? 'px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'
                : 'px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'"
            >
              {{ t()('nav.news') }}
            </button>
            <button
              type="button"
              (click)="scrollService.scrollToSection('formula-student')"
              [class]="scrollSpyService.isActive('formula-student')
                ? 'px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'
                : 'px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'"
            >
              {{ t()('nav.formulaStudent') }}
            </button>
            <a
              [routerLink]="['/season', this.data.currentSeason()]"
              [class]="isSeasonSectionActive()
                ? 'px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'
                : 'px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'"
            >
              {{ t()('nav.team') }}
            </a>
            <a
              routerLink="/partners"
              routerLinkActive="text-accent-500 bg-neutral-800"
              class="px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500"
            >
              {{ t()('nav.sponsoring') }}
            </a>
            <button
              type="button"
              (click)="scrollService.scrollToSection('contact')"
              [class]="scrollSpyService.isActive('contact')
                ? 'px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'
                : 'px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500'"
            >
              {{ t()('nav.contact') }}
            </button>
            <a
              routerLink="/team/join"
              class="block px-4 py-2 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-colors text-center"
            >
              {{ t()('nav.joinUs') }}
            </a>
          </div>

          <!-- Language Switcher -->
          <div class="relative">
            <button
              type="button"
              class="flex items-center gap-2 px-3 py-1.5 rounded bg-neutral-900 text-neutral-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500"
              aria-haspopup="menu"
              [attr.aria-expanded]="langOpen()"
              (click)="toggleLangDropdown()"
              (keydown.escape)="langOpen.set(false)"
            >
              <span class="sr-only">{{ t()('nav.language') }}</span>
              <span class="uppercase">{{ currentLang() }}</span>
              <svg class="w-4 h-4 text-neutral-400" viewBox="0 0 20 20" fill="none" stroke="currentColor">
                <path d="M6 8l4 4 4-4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>

            @if (langOpen()) {
              <div class="absolute right-0 mt-2 w-16 bg-neutral-900 border border-neutral-800 rounded shadow-lg z-50">
                <ul role="menu" aria-label="Language menu" class="py-1">
                  @for (lang of translationService.availableLanguages; track lang) {
                    <li>
                      <button
                        type="button"
                        role="menuitem"
                        class="w-full text-left px-4 py-2 text-sm hover:bg-neutral-800 focus:outline-none"
                        (click)="selectLanguage(lang)"
                      >
                        <span [class]="currentLang() === lang ? 'font-semibold text-accent-500' : 'text-neutral-300'">{{ lang.toUpperCase() }}</span>
                      </button>
                    </li>
                  }
                </ul>
              </div>
            }
          </div>

          <!-- Mobile menu button -->
          <button
            type="button"
            class="md:hidden p-2 text-neutral-400 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 rounded"
            aria-label="Toggle mobile menu"
            (click)="toggleMobileMenu()"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Navigation -->
      @if (mobileMenuOpen) {
        <div class="md:hidden bg-neutral-900 border-t border-neutral-800">
          <div class="px-4 py-3 space-y-2">
            <a
              routerLink="/"
              [class]="isHomeActive()
                ? 'block px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors'
                : 'block px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors'"
              (click)="closeMobileMenu()"
            >
              {{ t()('nav.home') }}
            </a>
            <!--
            <button
              type="button"
              (click)="scrollService.scrollToSection('events', '/', 0); closeMobileMenu()"
              [class]="scrollSpyService.isActive('events')
                ? 'w-full text-left block px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors cursor-pointer'
                : 'w-full text-left block px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors cursor-pointer'"
            >
              {{ t()('nav.events') }}
            </button> -->
            <button
              type="button"
              (click)="scrollService.scrollToSection('news'); closeMobileMenu()"
              [class]="scrollSpyService.isActive('news')
                ? 'w-full text-left block px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors cursor-pointer'
                : 'w-full text-left block px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors cursor-pointer'"
            >
              {{ t()('nav.news') }}
            </button>
            <button
              type="button"
              (click)="scrollService.scrollToSection('formula-student'); closeMobileMenu()"
              [class]="scrollSpyService.isActive('formula-student')
                ? 'w-full text-left block px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors cursor-pointer'
                : 'w-full text-left block px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors cursor-pointer'"
            >
              {{ t()('nav.formulaStudent') }}
            </button>
            <a
              [routerLink]="['/season', this.data.currentSeason()]"
              [class]="isSeasonSectionActive()
                ? 'block px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors'
                : 'block px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors'"
              (click)="closeMobileMenu()"
            >
              {{ t()('nav.team') }}
            </a>
            <a
              routerLink="/partners"
              routerLinkActive="text-accent-500 bg-neutral-800"
              class="block px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors"
              (click)="closeMobileMenu()"
            >
              {{ t()('nav.sponsoring') }}
            </a>
            <button
              type="button"
              (click)="scrollService.scrollToSection('contact'); closeMobileMenu()"
              [class]="scrollSpyService.isActive('contact')
                ? 'w-full text-left block px-4 py-2 rounded-lg text-accent-500 bg-neutral-800 transition-colors cursor-pointer'
                : 'w-full text-left block px-4 py-2 rounded-lg text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors cursor-pointer'"
            >
              {{ t()('nav.contact') }}
            </button>
            <a
              routerLink="/team/join"
              class="block px-4 py-2 bg-accent-600 hover:bg-accent-700 text-white font-semibold rounded-lg transition-colors text-center"
              (click)="closeMobileMenu()"
            >
              {{ t()('nav.joinUs') }}
            </a>
          </div>
        </div>
      }
    </nav>
  `,
})
export class NavbarComponent implements OnInit {
  protected readonly data = inject(DataService);
  protected readonly translationService = inject(TranslationService);
  protected readonly scrollService = inject(ScrollService);
  protected readonly scrollSpyService = inject(ScrollSpyService);
  private readonly router = inject(Router);

  protected readonly t = this.translationService.t;
  protected readonly currentLang = this.translationService.language;
  protected readonly currentRoute = signal<string>('');

  protected mobileMenuOpen = false;

  ngOnInit(): void {
    this.scrollSpyService.initialize();

    // Set initial route
    this.currentRoute.set(this.router.url);

    // Subscribe to router events to update current route
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentRoute.set(event.urlAfterRedirects);
      });
  }

  isHomeActive(): boolean {
    const url = this.currentRoute();
    // Home is active only when we're exactly on the root path (not on any subpage)
    const isOnHomePage = url === '/' || url === '';
    const noSectionActive = !this.scrollSpyService.isActive('events') &&
                           !this.scrollSpyService.isActive('news') &&
                           !this.scrollSpyService.isActive('formula-student') &&
                           !this.scrollSpyService.isActive('contact');
    return isOnHomePage && noSectionActive;
  }

  isSeasonSectionActive(): boolean {
    const path = this.currentRoute().split('?')[0].split('#')[0];
    return path === '/season' || path.startsWith('/season/');
  }

  switchLanguage(lang: Language): void {
    this.translationService.setLanguage(lang);
  }


  // Signal to track whether the language dropdown is open
  protected readonly langOpen = signal(false);

  toggleLangDropdown(): void {
    this.langOpen.update(v => !v);
  }

  selectLanguage(lang: Language): void {
    this.switchLanguage(lang);
    this.langOpen.set(false);
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

}
